/**
 * Correo con Resend (pedido de la dueña 2026-10-19; decisión «Correo con Resend: la única integración autorizada»).
 * Desde el sistema se envía al correo REGISTRADO del cliente la factura, la nota de entrega, la nota de crédito o de
 * débito, el cambio, la cotización, el recibo de abono y el pago del reintegro de una retención (con el comprobante del
 * pago que esté adjunto), con el formato ya armado en el cuerpo del correo. No se escribe otra dirección a mano.
 */
import { z } from 'zod';
import { puede, type Permiso, type Rol, type SujetoPermisos } from '../permisos.ts';
import { uuid } from './comun.ts';

/**
 * Qué se envía: un documento de venta (FAC, NOTA, NC, ND, CAM), una cotización, un recibo de abono, un reintegro o el
 * estado del retiro de una venta (al correo del cliente), o una orden de compra (al correo del proveedor, spec 21).
 * `retiro` lleva el id de la venta (FAC o NOTA): lo comprado, lo retirado y lo pendiente, sin precios (Fase 1 de
 * retiros, 2026-10-29).
 */
export const TIPOS_CORREO = ['documento', 'cotizacion', 'abono', 'reintegro', 'orden_compra', 'retiro'] as const;
export type TipoCorreo = (typeof TIPOS_CORREO)[number];

/** El mismo permiso que para ver o imprimir eso (nivel lectura): quien lo imprime, lo envía. */
export const PERMISO_CORREO: Record<TipoCorreo, Permiso> = {
  documento: 'ventas.emitir',
  cotizacion: 'cotizaciones.crear',
  abono: 'ventas.emitir',
  reintegro: 'retenciones-clientes.cargar',
  orden_compra: 'compras.registrar',
  // Lo envía quien entrega (caja, almacén, administración): es el estado de lo que retira el cliente
  retiro: 'inventario.entregas',
};

/**
 * Roles que no envían correos a los clientes aunque vean el documento: enviar es una acción hacia afuera y el contador
 * solo ve lo fiscal (revisión de seguridad 2026-10-19).
 */
export const ROLES_SIN_ENVIO_CORREO: readonly Rol[] = ['contador'];

/**
 * ¿Puede ENVIAR por correo eso? El permiso del tipo con nivel completo (quien solo lo ve en lectura no lo envía) y no
 * ser de un rol que no envía. Ver a quién va (`/correo/destinatario`) sigue pidiendo solo la lectura.
 */
export function puedeEnviarCorreo(sujeto: SujetoPermisos, tipo: TipoCorreo): boolean {
  return !ROLES_SIN_ENVIO_CORREO.includes(sujeto.rol) && puede(sujeto, PERMISO_CORREO[tipo], 'total');
}

/** El mismo documento no se reenvía antes de 2 minutos; cada usuario envía a lo sumo 20 correos cada 10 minutos. */
export const CORREO_REPETIDO_SEGUNDOS = 120;
export const CORREO_MAX_POR_USUARIO = 20;
export const CORREO_VENTANA_USUARIO_MS = 10 * 60_000;

/**
 * Tope de correos por día a un cliente (pedido del usuario 2026-11-02): a una misma dirección salen a lo sumo 3 correos
 * por día de Caracas, contando los enviados a mano y los automáticos. Se cuenta por DIRECCIÓN, no por ficha: si varias
 * fichas comparten el correo, comparten el tope. La orden de compra al proveedor no cuenta ni tiene tope.
 */
export const CORREOS_CLIENTE_POR_DIA = 3;

/** «Hoy ya se enviaron 3 correos a …»: el mensaje del tope, igual en el envío a mano y en los automáticos. */
export const mensajeTopeCorreos = (para: string) => `Hoy ya se enviaron ${CORREOS_CLIENTE_POR_DIA} correos a ${para}, el máximo por día`;

/** «2 correos», «1 correo»: lo que le queda hoy a una dirección. */
export const correosRestantesTexto = (n: number) => (n === 1 ? '1 correo' : `${n} correos`);

/** Enviar (y ver a quién va): solo qué y cuál. La dirección sale de la ficha del cliente, nunca del pedido. */
export const correoEnviarInput = z.object({ tipo: z.enum(TIPOS_CORREO), id: uuid }).strict();
export type CorreoEnviarInput = z.infer<typeof correoEnviarInput>;
export const correoDestinatarioQuery = z.object({ tipo: z.enum(TIPOS_CORREO), id: uuid });
export type CorreoDestinatarioQuery = z.infer<typeof correoDestinatarioQuery>;

/** Mensaje cuando el servidor no tiene la clave de Resend o el remitente (503 `correo_no_configurado`). */
export const MENSAJE_CORREO_NO_CONFIGURADO = 'El envío por correo no está configurado en el servidor: falta la clave de Resend o el remitente';

/** `GET /correo/estado`: si el servidor tiene la clave y el remitente. */
export interface CorreoEstadoDTO {
  configurado: boolean;
}

/**
 * `GET /correo/destinatario`: a qué correo iría y con qué asunto. Si no se puede enviar, `para` es null y `motivo` dice
 * por qué (sin correo registrado, consumidor final, anulado, tope del día…) con su `codigo`, el mismo que daría el envío.
 * `restantes_hoy`: cuántos correos más puede recibir hoy esa dirección (`CORREOS_CLIENTE_POR_DIA`); null si no aplica
 * (la orden de compra al proveedor, o no se puede enviar por otro motivo).
 */
export interface CorreoDestinatarioDTO {
  para: string | null;
  cliente: string;
  asunto: string;
  codigo: string | null;
  motivo: string | null;
  restantes_hoy: number | null;
}

/**
 * Respuesta del envío: a quién fue, con qué asunto y el identificador que dio Resend. `restantes_hoy`: cuántos correos
 * más puede recibir hoy esa dirección después de este (null en la orden de compra al proveedor).
 */
export interface CorreoEnviadoDTO {
  enviado: true;
  para: string;
  asunto: string;
  id: string;
  restantes_hoy: number | null;
}

/**
 * Lo que puede aparecer en el registro de correos: lo que se envía a mano más los correos que solo salen solos.
 * `orden_retiro` (Fase 4 de retiros, migración 083): «Retiro autorizado en …» con el código de retiro, el código nuevo,
 * la orden vencida o anulada. No se reenvía a mano: el código solo existe en el correo (la base guarda su hash).
 */
export const TIPOS_CORREO_REGISTRO = [...TIPOS_CORREO, 'orden_retiro'] as const;
export type TipoCorreoRegistro = (typeof TIPOS_CORREO_REGISTRO)[number];

/** Etiqueta de cada tipo en el registro de correos. */
export const TIPO_CORREO_ETIQUETA: Record<TipoCorreoRegistro, string> = {
  documento: 'Documento',
  cotizacion: 'Cotización',
  abono: 'Recibo de abono',
  reintegro: 'Pago del reintegro',
  orden_compra: 'Orden de compra',
  retiro: 'Estado de su retiro',
  orden_retiro: 'Retiro en otra sucursal',
};

/**
 * Registro de correos (Fase 1 de retiros, 2026-10-29): cada correo que sale del sistema queda en `correos_enviados`.
 * Se consulta por la venta (su sección «Correos») o por la ficha del cliente. Uno de los dos.
 */
export const correoRegistroQuery = z
  .object({ documento_id: uuid.optional(), cliente_id: uuid.optional() })
  .refine((q) => !!q.documento_id !== !!q.cliente_id, 'Indica la venta o el cliente');
export type CorreoRegistroQuery = z.infer<typeof correoRegistroQuery>;

/** Un correo del registro: cuándo, a quién, quién lo mandó (o automático), si salió y un resumen de lo que decía. */
export interface CorreoRegistroDTO {
  id: string;
  enviado_en: string;
  tipo: TipoCorreoRegistro;
  referencia: string | null;
  para: string;
  asunto: string;
  /**
   * Resumen de lo que se informó (nunca el HTML): `documento`, `motivo` (por qué salió uno automático), `renglones` y,
   * en el estado del retiro, `lineas` con lo comprado, lo retirado y lo pendiente.
   */
  contenido: Record<string, unknown>;
  adjuntos: string[];
  estado: 'enviado' | 'fallido';
  error: string | null;
  automatico: boolean;
  usuario_nombre: string | null;
  sucursal_nombre: string | null;
}

/**
 * Qué pasó con un correo automático «Estado de su retiro» (va en la respuesta de la entrega y de «Dejar pendiente»):
 * `enviado`, `fallido` (Resend no lo aceptó: queda en el registro), `sin_correo` (el cliente no tiene correo válido o
 * es consumidor final: se ofrece el texto para WhatsApp), `no_configurado` (el servidor no tiene Resend), `no_aplica`
 * (no hay nada que informar, p. ej. retiró todo de una vez) o `limite` (esa dirección ya recibió hoy los
 * `CORREOS_CLIENTE_POR_DIA` correos: no sale y no queda fila; el aviso de los días hábiles se intenta otro día).
 */
export interface CorreoAutomaticoDTO {
  estado: 'enviado' | 'fallido' | 'sin_correo' | 'no_configurado' | 'no_aplica' | 'limite';
  para: string | null;
  mensaje: string;
}

/** El estado del retiro en texto plano, para copiarlo y mandarlo por WhatsApp a mano (WhatsApp no se integra). */
export interface RetiroTextoDTO {
  asunto: string;
  texto: string;
}
